import React from 'react';
import { useIntl } from 'react-intl';
import { faCalendar } from '@fortawesome/pro-light-svg-icons/faCalendar';
import { StreamingAudienceResponse } from 'fym-shared/src/music24/streamingSections';
import { TypedMemo } from '../../../../common/types';
import { SMLButton } from '../../../components/primitives/SMLButton';
import { useStreamingPeriod } from '../../hooks/useStreamingPeriod';
import { formatDay, formatMonth } from '../../streamingFormat';
import { audienceMessages as messages } from '../../audienceMessages';
import { streamingPeriodMessages } from '../../streamingPeriodMessages';
import { SectionNoneCard } from '../../period/SectionNoneCard';

interface AudienceNoMonthProps {
  answer: StreamingAudienceResponse;
  sourceName: string;
  days: number;
  onShowMonth: (month: string) => void;
  onPickSource: (key: string) => void;
}

/**
 * Audience has no numbers for the period (design streaming-sections a3): no whole month in it, or
 * no rows of the picked source. "Show <month>" sets `?month=` and keeps the period.
 */
const AudienceNoMonthComponent: React.FC<AudienceNoMonthProps> = ({
  answer,
  sourceName,
  days,
  onShowMonth,
  onPickSource,
}) => {
  const intl = useIntl();
  const period = useStreamingPeriod();
  const start = formatDay(intl, answer.range.from);
  const end = formatDay(intl, answer.range.to);
  const noMonth = answer.months.length === 0;
  const other = answer.sources.find((option) => option.gives && option.key !== answer.source && option.number !== null);

  return (
    <SectionNoneCard
      icon={faCalendar}
      testId="streaming-audience-none"
      title={
        noMonth
          ? intl.formatMessage(streamingPeriodMessages.noWholeMonthTitle, { start, end })
          : intl.formatMessage(messages.noRowsTitle, { source: sourceName, start, end })
      }
      text={noMonth ? intl.formatMessage(messages.noWholeMonthText, { source: sourceName }) : undefined}
    >
      {answer.newestMonth !== null ? (
        <SMLButton variant="primary" onClick={() => onShowMonth(answer.newestMonth ?? '')}>
          {intl.formatMessage(streamingPeriodMessages.showMonth, { month: formatMonth(intl, answer.newestMonth) })}
        </SMLButton>
      ) : null}
      {noMonth && days < 90 ? (
        <SMLButton onClick={() => period.setDays(90)}>
          {intl.formatMessage(streamingPeriodMessages.pickDays, { days: 90 })}
        </SMLButton>
      ) : null}
      {!noMonth && other !== undefined ? (
        <SMLButton onClick={() => onPickSource(other.key)}>
          {intl.formatMessage(streamingPeriodMessages.showOtherSource)}
        </SMLButton>
      ) : null}
    </SectionNoneCard>
  );
};

export const AudienceNoMonth = TypedMemo(AudienceNoMonthComponent);
